import React, { ReactNode, useEffect, useReducer, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

type DrawerState = {
  modalId: string;
  isVisible: boolean;
  setIsVisible: (isVisible: boolean, id: string | null | undefined) => void;
  setId: (id: string) => void;
};

enum DrawerActionType {
  SET_IS_VISIBLE,
  SET_MODAL_ID,
}

const initDrawerState: DrawerState = {
  modalId: 'empty',
  isVisible: false,
  setIsVisible: (_isVisible, _modalId) => {},
  setId: _modalId => {},
};

interface IProps {
  children: ReactNode;
}

export type DrawerAction =
  | { type: DrawerActionType.SET_IS_VISIBLE; isVisible: boolean; modalId: string | null }
  | { type: DrawerActionType.SET_MODAL_ID; modalId: string };

const MODAL = 'modal';
const EMPTY = 'empty';

export const useDrawerInternal = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [modalId, setModalId] = useState<string | null>(searchParams.get(MODAL));
  const [isDisplayed, setIsDisplayed] = useState<boolean>(modalId !== null);

  const setIsVisible = (isVisible: boolean, id: string | null) => {
    setIsDisplayed(isVisible);

    if (isVisible) {
      setModalId(id);
      searchParams.set(MODAL, id ?? EMPTY);
      setSearchParams(searchParams);
    } else {
      setModalId(null);
      searchParams.delete(MODAL);
      setSearchParams(searchParams);
    }
  };

  return {
    modalId,
    isDisplayed,
    setIsDisplayed: setIsVisible,
    setModalId,
  };
};

export const DrawerContext = React.createContext(initDrawerState);

const drawerReducer: React.Reducer<DrawerState, DrawerAction> = (state, action) => {
  switch (action.type) {
    case DrawerActionType.SET_IS_VISIBLE:
      return { ...state, isVisible: action.isVisible, modalId: action.modalId ?? EMPTY };
    case DrawerActionType.SET_MODAL_ID:
      return { ...state, modalId: action.modalId };
    default:
      return state;
  }
};

export const DrawerProvider = ({ children }: IProps) => {
  const { modalId: modalValue, isDisplayed, setIsDisplayed, setModalId } = useDrawerInternal();
  const [searchParams] = useSearchParams();
  const [state, dispatch] = useReducer(drawerReducer, {
    ...initDrawerState,
    modalId: modalValue ?? EMPTY,
    isVisible: isDisplayed,
  });

  useEffect(() => {
    const modalId = searchParams.get(MODAL);

    dispatch({
      type: DrawerActionType.SET_IS_VISIBLE,
      isVisible: !!modalId,
      modalId: modalId ?? EMPTY,
    });
  }, [searchParams]);

  const setIsVisible = (isVisible: boolean, modalId: string | null = null) => {
    setIsDisplayed(isVisible, modalId);
    dispatch({ type: DrawerActionType.SET_IS_VISIBLE, isVisible, modalId });
  };

  const setId = (modalId: string) => {
    setModalId(modalId);
    dispatch({ type: DrawerActionType.SET_MODAL_ID, modalId });
  };

  const value: DrawerState = {
    ...state,
    setIsVisible,
    setId,
  };

  return <DrawerContext.Provider value={value}>{children}</DrawerContext.Provider>;
};
